// Guard against browser page-translation (Google Translate / Safari Translate)
// crashing the React app.
//
// Translators rewrite the page by swapping text nodes — wrapping them in <font>
// or <span> wrappers and detaching the originals. React keeps references to the
// ORIGINAL nodes, so its next reconciliation calls removeChild/insertBefore on a
// node the translator has already re-parented. The DOM then throws
//   "Failed to execute 'removeChild' on 'Node': The node to be removed is not a
//    child of this node."
// (or the insertBefore equivalent), which unmounts the whole subtree to our
// ErrorBoundary crash card. Reloading doesn't help — the translator re-applies on
// every render.
//
// Making these two operations degrade gracefully when the node isn't actually a
// child of `this` keeps the app alive under translation. This is the widely-used
// community mitigation for facebook/react#11538; it only changes behaviour in the
// already-broken (throwing) case, so untranslated pages are unaffected.

export const installDomTranslateGuard = (): void => {
  if (typeof Node !== "function" || !Node.prototype) return;
  const marker = Node.prototype as unknown as { __phTranslateGuard?: boolean };
  if (marker.__phTranslateGuard) return;
  marker.__phTranslateGuard = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // The translator already detached/replaced this node — nothing to remove.
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was moved by the translator — append instead of throwing.
      return this.appendChild(newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  } as typeof Node.prototype.insertBefore;

  const originalReplaceChild = Node.prototype.replaceChild;
  Node.prototype.replaceChild = function <T extends Node>(
    this: Node,
    newChild: Node,
    oldChild: T,
  ): T {
    if (oldChild.parentNode !== this) {
      // The node React means to replace was already swapped out by the translator.
      // Insert the new child so nothing is lost, and report the old one as removed.
      this.appendChild(newChild);
      return oldChild;
    }
    return originalReplaceChild.call(this, newChild, oldChild) as T;
  } as typeof Node.prototype.replaceChild;
};
